import { useEffect, useState } from "react";
import { api } from "../lib/api.js";
import { useAuth } from "../lib/auth.jsx";

/**
 * AccountPage — modern self-service hub.
 *
 * Layout:
 *   ┌────────────────────────────────────────────────┐
 *   │  Hero card: avatar + name + email + chips      │
 *   ├──────────────────────┬─────────────────────────┤
 *   │  Profile             │  Vorks ID status        │
 *   │  - display name      │  - linked / not linked  │
 *   │                      │  - migrate / unlink     │
 *   ├──────────────────────┴─────────────────────────┤
 *   │  Password (or Vorks-managed notice)            │
 *   └────────────────────────────────────────────────┘
 */
export default function AccountPage() {
  const { logout } = useAuth();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  async function reload() {
    try {
      const u = await api("/auth/me");
      setMe(u);
    } catch (e) {
      console.warn("Failed to load /me:", e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { reload(); }, []);

  if (loading || !me) {
    return (
      <div style={{
        padding: 48, textAlign: "center",
        color: "var(--text-dim)", fontFamily: "var(--mono)", fontSize: 12,
      }}>
        Lade Account…
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 880, margin: "0 auto", padding: "8px 0 40px" }}>
      <HeroCard me={me} />

      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 16,
        marginBottom: 16,
      }}>
        <ProfileCard me={me} onReload={reload} />
        <VorksIDCard me={me} onReload={reload} />
      </div>

      <PasswordCard me={me} onLogout={logout} />
    </div>
  );
}

// ─── Hero ────────────────────────────────────────────────────────────────

function HeroCard({ me }) {
  const initial = (me.display_name || me.email || "?").charAt(0).toUpperCase();
  return (
    <div style={{
      position: "relative",
      padding: "28px 28px 24px",
      marginBottom: 16,
      background: "linear-gradient(135deg, rgba(96,165,250,0.06) 0%, rgba(168,85,247,0.04) 50%, rgba(229,57,53,0.06) 100%)",
      border: "1px solid var(--line)",
      borderRadius: 12,
      overflow: "hidden",
    }}>
      {/* Subtle grid background */}
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.04) 1px, transparent 0)",
        backgroundSize: "18px 18px",
        pointerEvents: "none",
      }} />

      <div style={{
        position: "relative", display: "flex", alignItems: "center", gap: 18,
      }}>
        <Avatar initial={initial} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 24, fontWeight: 600,
            letterSpacing: "-0.02em",
            color: "var(--text)",
            marginBottom: 2,
          }}>
            {me.display_name || me.email}
          </div>
          <div className="mono" style={{
            fontSize: 12, color: "var(--text-dim)",
            display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
          }}>
            <span>{me.email}</span>
            <span style={{ opacity: 0.5 }}>·</span>
            <RoleBadge role={me.role} />
            {me.has_vorks_id && (
              <>
                <span style={{ opacity: 0.5 }}>·</span>
                <VorksLinkedBadge />
              </>
            )}
          </div>
          <div className="mono" style={{
            fontSize: 10, color: "var(--text-faint)",
            marginTop: 8,
          }}>
            seit {formatDate(me.created_at)}
            {me.last_login_at && <> · letzter Login {relativeTime(me.last_login_at)}</>}
          </div>
        </div>
      </div>
    </div>
  );
}

function Avatar({ initial }) {
  // Generate a hash-based gradient so each user gets a stable, distinct look
  const colors = [
    ["#60a5fa", "#a78bfa"], // blue→violet
    ["#34d399", "#22d3ee"], // green→cyan
    ["#fbbf24", "#fb923c"], // amber→orange
    ["#f472b6", "#e879f9"], // pink→fuchsia
    ["#a3e635", "#84cc16"], // lime
  ];
  const hash = initial.charCodeAt(0) % colors.length;
  const [from, to] = colors[hash];
  return (
    <div style={{
      width: 64, height: 64, borderRadius: 18,
      background: `linear-gradient(135deg, ${from} 0%, ${to} 100%)`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: 28, fontWeight: 700, color: "#0a0a0a",
      fontFamily: "var(--mono)",
      boxShadow: `0 8px 24px ${from}40`,
      flexShrink: 0,
    }}>
      {initial}
    </div>
  );
}

function RoleBadge({ role }) {
  const colors = {
    superadmin: { bg: "rgba(244,114,182,0.15)", border: "rgba(244,114,182,0.4)", fg: "#f472b6" },
    admin:      { bg: "rgba(168,85,247,0.15)",  border: "rgba(168,85,247,0.4)",  fg: "#c084fc" },
    moderator:  { bg: "rgba(96,165,250,0.15)",  border: "rgba(96,165,250,0.4)",  fg: "#60a5fa" },
    user:       { bg: "rgba(255,255,255,0.06)", border: "var(--line)",           fg: "var(--text-dim)" },
  };
  const c = colors[role] || colors.user;
  return (
    <span style={{
      padding: "2px 8px", borderRadius: 4,
      background: c.bg, border: `1px solid ${c.border}`,
      color: c.fg, fontSize: 10, letterSpacing: "0.08em",
      textTransform: "uppercase",
    }}>{role}</span>
  );
}

function VorksLinkedBadge() {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "2px 8px", borderRadius: 4,
      background: "rgba(229,57,53,0.12)",
      border: "1px solid rgba(229,57,53,0.4)",
      color: "#fca5a5",
      fontSize: 10, letterSpacing: "0.08em",
    }}>
      <span style={{
        width: 12, height: 12, borderRadius: 3,
        background: "#e53935",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        fontSize: 8, fontWeight: 700, color: "#fff",
      }}>V</span>
      VORKS ID
    </span>
  );
}

// ─── Profile card ────────────────────────────────────────────────────────

function ProfileCard({ me, onReload }) {
  const [name, setName] = useState(me.display_name || "");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  async function save() {
    if (!name.trim() || name.trim() === me.display_name) return;
    setBusy(true); setMsg(null);
    try {
      await api("/auth/me", { method: "PUT", body: { display_name: name.trim() } });
      setMsg({ kind: "ok", text: "Gespeichert" });
      setTimeout(() => setMsg(null), 2500);
      await onReload();
    } catch (e) {
      setMsg({ kind: "err", text: e.message });
    } finally {
      setBusy(false);
    }
  }

  const dirty = name.trim() && name.trim() !== me.display_name;

  return (
    <Card icon="◐" title="Profil" subtitle="Anzeigename ändern">
      <div style={{ display: "grid", gap: 10 }}>
        <Field
          label="Anzeigename"
          value={name}
          onChange={setName}
          disabled={busy}
        />
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          minHeight: 30,
        }}>
          {msg ? (
            <span className="mono" style={{
              fontSize: 11,
              color: msg.kind === "ok" ? "var(--ok)" : "var(--err)",
            }}>{msg.text}</span>
          ) : <span />}
          <button
            className="btn primary"
            onClick={save}
            disabled={busy || !dirty}
            style={{ fontSize: 11, padding: "6px 14px" }}
          >
            {busy ? "…" : "Speichern"}
          </button>
        </div>
      </div>
    </Card>
  );
}

// ─── Vorks ID card ───────────────────────────────────────────────────────

function VorksIDCard({ me, onReload }) {
  const [busy, setBusy] = useState(false);
  const [migrateResult, setMigrateResult] = useState(null);
  const [msg, setMsg] = useState(null);

  const [showUnlink, setShowUnlink] = useState(false);
  const [unlinkPw, setUnlinkPw] = useState("");
  const [unlinkPw2, setUnlinkPw2] = useState("");

  if (!me.vorks_id_configured) {
    return (
      <Card icon="V" iconBg="#e53935" title="Vorks ID" subtitle="Single Sign-On">
        <div style={{
          padding: 10, borderRadius: 4,
          background: "rgba(255,255,255,0.03)",
          border: "1px solid var(--line)",
          fontSize: 11, color: "var(--text-dim)", lineHeight: 1.5,
        }}>
          Vorks ID ist auf diesem Server nicht konfiguriert.
        </div>
      </Card>
    );
  }

  async function migrate() {
    setBusy(true); setMsg(null); setMigrateResult(null);
    try {
      const r = await api("/auth/migrate-to-vorks-id", { method: "POST" });
      setMigrateResult(r);
      if (r.status === "linked") await onReload();
    } catch (e) {
      setMsg({ kind: "err", text: e.message });
    } finally {
      setBusy(false);
    }
  }

  async function unlink() {
    if (unlinkPw !== unlinkPw2) {
      setMsg({ kind: "err", text: "Passwörter stimmen nicht überein" }); return;
    }
    if (unlinkPw.length < 8) {
      setMsg({ kind: "err", text: "Mindestens 8 Zeichen" }); return;
    }
    setBusy(true); setMsg(null);
    try {
      await api("/auth/unlink-vorks-id", {
        method: "POST", body: { new_password: unlinkPw },
      });
      setMsg({ kind: "ok", text: "Verknüpfung entfernt" });
      setShowUnlink(false);
      setUnlinkPw(""); setUnlinkPw2("");
      await onReload();
    } catch (e) {
      setMsg({ kind: "err", text: e.message });
    } finally {
      setBusy(false);
    }
  }

  // ── Linked state ─────────────────────────────────────────────────
  if (me.has_vorks_id) {
    return (
      <Card icon="V" iconBg="#e53935" title="Vorks ID"
            subtitle="Single Sign-On"
            badge={<StatusDot color="var(--ok)" label="verknüpft" />}>
        <div style={{
          padding: 10, marginBottom: 12,
          background: "rgba(74,222,128,0.06)",
          border: "1px solid rgba(74,222,128,0.25)",
          borderRadius: 4,
          fontSize: 11, color: "var(--text)", lineHeight: 1.5,
        }}>
          Dein Account ist mit Vorks ID verknüpft. Passwortänderungen werden auf
          {" "}<a href="https://accounts.vorks.net/account" target="_blank" rel="noreferrer"
                  style={{ color: "#facc15", textDecoration: "none", fontWeight: 500 }}>
            accounts.vorks.net
          </a> verwaltet.
        </div>

        {!showUnlink && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <a
              href="https://accounts.vorks.net/account"
              target="_blank"
              rel="noreferrer"
              className="btn primary"
              style={{
                fontSize: 11, padding: "6px 12px",
                textDecoration: "none", flex: 1, textAlign: "center",
                background: "#e53935", borderColor: "#e53935",
              }}
            >
              Vorks ID öffnen ↗
            </a>
            {me.vorks_id_allow_unlink && (
              <button
                className="btn ghost"
                onClick={() => setShowUnlink(true)}
                style={{ fontSize: 11, padding: "6px 12px" }}
                disabled={busy}
              >
                Trennen
              </button>
            )}
          </div>
        )}

        {!me.vorks_id_allow_unlink && !showUnlink && (
          <div style={{
            marginTop: 10, padding: 8,
            background: "rgba(255,255,255,0.02)",
            border: "1px solid var(--line)",
            borderRadius: 4,
            fontSize: 10, color: "var(--text-faint)",
            fontFamily: "var(--mono)",
          }}>
            🔒 Trennung vom Administrator deaktiviert
          </div>
        )}

        {showUnlink && (
          <div style={{
            marginTop: 10, padding: 12,
            border: "1px solid rgba(239,68,68,0.3)",
            background: "rgba(239,68,68,0.04)",
            borderRadius: 6, display: "grid", gap: 8,
          }}>
            <div style={{ fontSize: 11, color: "var(--text)", lineHeight: 1.5 }}>
              Setze ein neues lokales Passwort. Damit kannst du dich ohne Vorks ID einloggen.
            </div>
            <Field label="Neues Passwort" type="password" value={unlinkPw} onChange={setUnlinkPw} disabled={busy} />
            <Field label="Bestätigen" type="password" value={unlinkPw2} onChange={setUnlinkPw2} disabled={busy} />
            <div style={{ display: "flex", gap: 6 }}>
              <button className="btn ghost" onClick={() => { setShowUnlink(false); setUnlinkPw(""); setUnlinkPw2(""); }}
                      disabled={busy} style={{ fontSize: 11, padding: "5px 10px" }}>
                Abbrechen
              </button>
              <button className="btn primary danger"
                      onClick={unlink}
                      disabled={busy || !unlinkPw || !unlinkPw2}
                      style={{ fontSize: 11, padding: "5px 10px", flex: 1 }}>
                {busy ? "…" : "Endgültig trennen"}
              </button>
            </div>
          </div>
        )}

        {msg && (
          <div className="mono" style={{
            fontSize: 11, marginTop: 8,
            color: msg.kind === "ok" ? "var(--ok)" : "var(--err)",
          }}>{msg.text}</div>
        )}
      </Card>
    );
  }

  // ── Not linked state ─────────────────────────────────────────────
  return (
    <Card icon="V" iconBg="#e53935" title="Vorks ID"
          subtitle="Single Sign-On"
          badge={<StatusDot color="var(--text-faint)" label="nicht verknüpft" />}>
      {migrateResult?.status === "pending_setup" ? (
        <div style={{
          padding: 12,
          background: "rgba(74,222,128,0.06)",
          border: "1px solid rgba(74,222,128,0.25)",
          borderRadius: 4, fontSize: 11, lineHeight: 1.5,
          color: "var(--text)",
        }}>
          ✓ Eine E-Mail mit einem Setup-Link wurde an{" "}
          <span className="mono" style={{ color: "var(--ok)" }}>{me.email}</span>{" "}
          gesendet. Klicke den Link, setze dein Passwort und melde dich erneut bei Vorks Connect an.
        </div>
      ) : migrateResult?.status === "linked" ? (
        <div style={{
          padding: 12,
          background: "rgba(74,222,128,0.06)",
          border: "1px solid rgba(74,222,128,0.25)",
          borderRadius: 4, fontSize: 11, lineHeight: 1.5,
          color: "var(--text)",
        }}>
          ✓ Account mit existierender Vorks ID verknüpft.
        </div>
      ) : (
        <>
          <div style={{
            fontSize: 11, color: "var(--text-dim)", lineHeight: 1.6,
            marginBottom: 12,
          }}>
            Stufe deinen Account zu Vorks ID hoch, um dich überall im
            Vorks-Ökosystem mit nur einem Login anzumelden.
          </div>
          {me.vorks_id_allow_migrate ? (
            <button
              className="btn primary"
              onClick={migrate}
              disabled={busy}
              style={{
                width: "100%", fontSize: 12, padding: "8px 14px",
                background: "#e53935", borderColor: "#e53935",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}
            >
              <span style={{
                width: 16, height: 16, borderRadius: 3,
                background: "rgba(0,0,0,0.25)",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                fontSize: 9, fontWeight: 700, color: "#fff",
              }}>V</span>
              {busy ? "Bitte warten…" : "Zu Vorks ID hochstufen"}
            </button>
          ) : (
            <div style={{
              padding: 10,
              background: "rgba(255,255,255,0.02)",
              border: "1px solid var(--line)",
              borderRadius: 4,
              fontSize: 11, color: "var(--text-faint)",
              fontFamily: "var(--mono)",
              textAlign: "center",
            }}>
              🔒 Migration vom Administrator deaktiviert
            </div>
          )}
        </>
      )}

      {msg && (
        <div className="mono" style={{
          fontSize: 11, marginTop: 10,
          color: msg.kind === "ok" ? "var(--ok)" : "var(--err)",
        }}>{msg.text}</div>
      )}
    </Card>
  );
}

// ─── Password card ───────────────────────────────────────────────────────

function PasswordCard({ me, onLogout }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [expanded, setExpanded] = useState(false);

  // Locked when SSO is the source of truth
  const locked = me.has_vorks_id;

  if (locked) {
    return (
      <Card icon="●" title="Passwort" subtitle="Verwaltet via Vorks ID">
        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: 12,
          background: "rgba(229,57,53,0.06)",
          border: "1px solid rgba(229,57,53,0.25)",
          borderRadius: 6,
        }}>
          <div style={{
            width: 28, height: 28, borderRadius: 6,
            background: "rgba(229,57,53,0.2)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 14, flexShrink: 0,
          }}>🔒</div>
          <div style={{ flex: 1, minWidth: 0, fontSize: 12, lineHeight: 1.5, color: "var(--text)" }}>
            Passwortänderungen werden auf <strong>accounts.vorks.net</strong> verwaltet.
            Dein lokales Passwort hier wurde deaktiviert.
          </div>
        </div>
      </Card>
    );
  }

  async function change() {
    if (next !== confirm) {
      setMsg({ kind: "err", text: "Passwörter stimmen nicht überein" }); return;
    }
    if (next.length < 8) {
      setMsg({ kind: "err", text: "Mindestens 8 Zeichen" }); return;
    }
    setBusy(true); setMsg(null);
    try {
      await api("/auth/change-password", {
        method: "POST",
        body: { current_password: current, new_password: next },
      });
      setMsg({ kind: "ok", text: "Passwort geändert — du wirst neu eingeloggt..." });
      setTimeout(() => onLogout(), 1500);
    } catch (e) {
      let text = e.message;
      if (text && text.includes("current_password_wrong")) text = "Aktuelles Passwort falsch";
      setMsg({ kind: "err", text });
    } finally {
      setBusy(false);
    }
  }

  if (!expanded) {
    return (
      <Card icon="●" title="Passwort" subtitle="Lokales Passwort ändern">
        <button
          className="btn ghost"
          onClick={() => setExpanded(true)}
          style={{ fontSize: 11, padding: "6px 14px" }}
        >
          Passwort ändern
        </button>
      </Card>
    );
  }

  return (
    <Card icon="●" title="Passwort" subtitle="Lokales Passwort ändern">
      <div style={{ display: "grid", gap: 10 }}>
        <Field label="Aktuelles Passwort" type="password" value={current} onChange={setCurrent} disabled={busy} autoComplete="current-password" />
        <Field label="Neues Passwort" type="password" value={next} onChange={setNext} disabled={busy} autoComplete="new-password" hint="Mindestens 8 Zeichen" />
        <Field label="Bestätigen" type="password" value={confirm} onChange={setConfirm} disabled={busy} autoComplete="new-password" />

        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginTop: 2,
        }}>
          {msg ? (
            <span className="mono" style={{
              fontSize: 11,
              color: msg.kind === "ok" ? "var(--ok)" : "var(--err)",
            }}>{msg.text}</span>
          ) : <span />}
          <div style={{ display: "flex", gap: 6 }}>
            <button
              className="btn ghost"
              onClick={() => { setExpanded(false); setCurrent(""); setNext(""); setConfirm(""); setMsg(null); }}
              disabled={busy}
              style={{ fontSize: 11, padding: "5px 10px" }}
            >
              Abbrechen
            </button>
            <button
              className="btn primary"
              onClick={change}
              disabled={busy || !current || !next || !confirm}
              style={{ fontSize: 11, padding: "5px 12px" }}
            >
              {busy ? "…" : "Passwort setzen"}
            </button>
          </div>
        </div>
      </div>
    </Card>
  );
}

// ─── Generic UI primitives ───────────────────────────────────────────────

function Card({ icon, iconBg, title, subtitle, badge, children }) {
  return (
    <div style={{
      padding: 18,
      background: "var(--bg-elev)",
      border: "1px solid var(--line)",
      borderRadius: 10,
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        marginBottom: 14, paddingBottom: 14,
        borderBottom: "1px solid var(--line)",
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: iconBg || "rgba(255,255,255,0.06)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: iconBg ? 13 : 14, fontWeight: 700,
          color: iconBg ? "#fff" : "var(--text-dim)",
          fontFamily: iconBg ? "var(--mono)" : "inherit",
          flexShrink: 0,
        }}>
          {icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em" }}>
            {title}
          </div>
          {subtitle && (
            <div style={{
              fontSize: 10, color: "var(--text-dim)",
              fontFamily: "var(--mono)",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              marginTop: 2,
            }}>
              {subtitle}
            </div>
          )}
        </div>
        {badge}
      </div>
      {children}
    </div>
  );
}

function StatusDot({ color, label }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      fontSize: 10, fontFamily: "var(--mono)",
      color: "var(--text-dim)",
      textTransform: "uppercase", letterSpacing: "0.08em",
      whiteSpace: "nowrap",
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: "50%",
        background: color,
        boxShadow: color !== "var(--text-faint)" ? `0 0 6px ${color}` : "none",
      }} />
      {label}
    </span>
  );
}

function Field({ label, value, onChange, type = "text", disabled, autoComplete, hint }) {
  return (
    <div>
      <label style={{
        display: "block", fontSize: 9, color: "var(--text-dim)",
        textTransform: "uppercase", letterSpacing: "0.1em",
        marginBottom: 4, fontFamily: "var(--mono)",
        fontWeight: 500,
      }}>
        {label}
      </label>
      <input
        type={type}
        value={value || ""}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        disabled={disabled}
        autoComplete={autoComplete}
        style={{
          width: "100%",
          padding: "8px 10px",
          fontFamily: type === "password" ? "var(--mono)" : "inherit",
          fontSize: 12,
          background: "var(--bg)",
          border: "1px solid var(--line)",
          borderRadius: 4,
          color: "var(--text)",
        }}
      />
      {hint && (
        <div style={{ fontSize: 10, color: "var(--text-faint)", marginTop: 4, fontFamily: "var(--mono)" }}>
          {hint}
        </div>
      )}
    </div>
  );
}

// ─── Time helpers ────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return "—";
  const normalized = typeof iso === "string" && !iso.endsWith("Z") && !/[+-]\d{2}:?\d{2}$/.test(iso)
    ? iso.replace(" ", "T") + "Z"
    : iso;
  return new Date(normalized).toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  });
}

function relativeTime(iso) {
  if (!iso) return "—";
  const normalized = typeof iso === "string" && !iso.endsWith("Z") && !/[+-]\d{2}:?\d{2}$/.test(iso)
    ? iso.replace(" ", "T") + "Z"
    : iso;
  const sec = Math.max(0, Math.floor((Date.now() - new Date(normalized).getTime()) / 1000));
  if (sec < 60) return `vor ${sec}s`;
  if (sec < 3600) return `vor ${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `vor ${Math.floor(sec / 3600)}h`;
  return `vor ${Math.floor(sec / 86400)}d`;
}
